import React, { useCallback, useEffect, useState } from 'react';
import { Linking, Pressable, ScrollView, StyleSheet, Text, TextInput, View, } from 'react-native';
import { FittedLogo } from '../components/FittedLogo';
import { useDispatch } from 'react-redux';
import { ContentView } from '../components/ContentView';
import { Button } from '../components/Button';
import { LOGO_HEIGHT, LOGO_WIDTH } from '../components/consts';
import { setEmail } from '../redux/appSlice';
import { useChangePendingEmailMutation, useResendConfirmationMutation, } from '../services/abbiWayApi';
import { theme } from '../theme';
const SUPPORT_EMAIL = 'abbiswayinfo@gmail.com';
const PAGE_MAX_WIDTH = 520;
const CARD_MAX_WIDTH = 480;
const parseValidationErrors = (err) => {
    const e = err;
    const data = e?.data;
    return data?.errors;
};
export const PendingEmailConfirmationScreen = ({ route }) => {
    const dispatch = useDispatch();
    const [displayEmail, setDisplayEmail] = useState(() => route.params.email.trim().toLowerCase());
    const [statusMessage, setStatusMessage] = useState(null);
    const [changeExpanded, setChangeExpanded] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [changePassword, setChangePassword] = useState('');
    const [cooldownLeft, setCooldownLeft] = useState(0);
    const [resend, { isLoading: resendLoading }] = useResendConfirmationMutation();
    const [changeEmailMutation, { isLoading: changeLoading }] = useChangePendingEmailMutation();
    useEffect(() => {
        if (cooldownLeft <= 0) {
            return;
        }
        const t = setInterval(() => {
            setCooldownLeft(s => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => {
            clearInterval(t);
        };
    }, [cooldownLeft]);
    const onResend = useCallback(async () => {
        setStatusMessage(null);
        if (cooldownLeft > 0) {
            setStatusMessage(`You can request another email in ${cooldownLeft} seconds.`);
            return;
        }
        try {
            await resend({ email: displayEmail }).unwrap();
            setStatusMessage('If an account is still waiting on confirmation at this address, we sent a fresh link. Check inbox and spam.');
        }
        catch (err) {
            const errors = parseValidationErrors(err);
            if (errors?.confirmationEmail === 'recentlySent') {
                const sec = parseInt(String(errors.retryAfterSeconds ?? ''), 10);
                if (Number.isFinite(sec) && sec > 0) {
                    setCooldownLeft(sec);
                    setStatusMessage(`Please wait ${sec} seconds before requesting another confirmation email.`);
                    return;
                }
            }
            if (errors?.email === 'alreadyConfirmed') {
                setStatusMessage('This email looks already confirmed. Try signing in instead.');
                return;
            }
            if (errors?.email === 'pendingUserNotFound') {
                setStatusMessage('We could not find a pending signup for this address. Check for typos or start signup again.');
                return;
            }
            if (errors?.confirmationEmail === 'sendFailed') {
                setStatusMessage('We could not send the email right now. Try again in a few minutes or contact support.');
                return;
            }
            setStatusMessage('Something went wrong. Please try again.');
        }
    }, [cooldownLeft, displayEmail, resend]);
    const onChangeEmail = useCallback(async () => {
        setStatusMessage(null);
        const trimmedNew = newEmail.trim().toLowerCase();
        if (!trimmedNew || !changePassword) {
            setStatusMessage('Enter your password and the new email address.');
            return;
        }
        try {
            await changeEmailMutation({
                currentEmail: displayEmail,
                password: changePassword,
                newEmail: trimmedNew,
            }).unwrap();
            dispatch(setEmail(trimmedNew));
            setDisplayEmail(trimmedNew);
            setNewEmail('');
            setChangePassword('');
            setChangeExpanded(false);
            setStatusMessage(`We updated your address to ${trimmedNew} and sent a new confirmation link.`);
        }
        catch (err) {
            const errors = parseValidationErrors(err);
            if (errors?.emailOrPassword === 'invalidCredentials') {
                setStatusMessage('Password did not match. Try again.');
                return;
            }
            if (errors?.newEmail === 'emailAlreadyExists') {
                setStatusMessage('That email is already in use. Choose a different one.');
                return;
            }
            if (errors?.newEmail === 'mustBeDifferent') {
                setStatusMessage('New email must be different from the current one.');
                return;
            }
            if (errors?.email === 'alreadyConfirmed') {
                setStatusMessage('This account is already confirmed. Try signing in instead.');
                return;
            }
            if (errors?.confirmationEmail === 'sendFailed') {
                setStatusMessage('Email was updated but we could not send confirmation. Use Resend or contact support.');
                return;
            }
            if (errors?.email === 'notEmailProvider') {
                setStatusMessage('This signup is not on email sign-in. Contact support if you need help.');
                return;
            }
            if (errors && Object.keys(errors).length > 0) {
                const first = Object.values(errors).find(v => typeof v === 'string' && v.length > 0);
                if (first) {
                    setStatusMessage(first);
                    return;
                }
            }
            setStatusMessage('Could not update email. Please try again.');
        }
    }, [
        changeEmailMutation,
        changePassword,
        dispatch,
        displayEmail,
        newEmail,
    ]);
    return (<ContentView style={styles.outer}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={styles.pageColumn}>
          <FittedLogo style={styles.logo}/>
          <View style={styles.card}>
            <Text style={styles.title}>Check your email</Text>
            <Text style={styles.lead}>
              We sent a confirmation link to{' '}
              <Text style={styles.emailEmphasis}>{displayEmail}</Text>. Open it
              on this device (or any device where you use email) to finish
              activating your account.
            </Text>
            <Text style={styles.body}>
              Most messages arrive within one to two minutes. Check your inbox,
              then your Spam or Promotions folder.
            </Text>
            <Text style={styles.bodyMuted}>
              Gmail: Promotions or Spam. Outlook: Junk Email. Yahoo: Spam. You
              can search for &quot;Abbi&apos;s Way&quot; or the subject
              &quot;Confirm your Abbi&apos;s Way account&quot;.
            </Text>
            <View style={styles.actions}>
              <Button title={resendLoading
            ? 'Sending…'
            : cooldownLeft > 0
                ? `Resend available in ${cooldownLeft}s`
                : 'Resend confirmation email'} loading={resendLoading} disabled={resendLoading || cooldownLeft > 0} onPress={onResend}/>
            </View>
            <Pressable accessibilityRole="button" onPress={() => {
            setChangeExpanded(e => !e);
            setStatusMessage(null);
        }} style={({ pressed }) => [
            styles.linkRow,
            pressed ? styles.linkRowPressed : null,
        ]}>
              <Text style={styles.linkText}>
                {changeExpanded
            ? 'Hide change-email form'
            : 'Wrong email address?'}
              </Text>
            </Pressable>
            {changeExpanded ? (<View style={styles.changeBox}>
                <Text style={styles.changeHeading}>Update your email</Text>
                <Text style={styles.label}>New email</Text>
                <TextInput style={styles.input} value={newEmail} onChangeText={setNewEmail} autoCapitalize="none" keyboardType="email-address" autoComplete="email" placeholder="corrected@example.com"/>
                <Text style={styles.label}>Password (from signup)</Text>
                <TextInput style={styles.input} value={changePassword} onChangeText={setChangePassword} secureTextEntry autoComplete="password" placeholder="Your password"/>
                <View style={styles.actionsTight}>
                  <Button title={changeLoading ? 'Updating…' : 'Update email & resend'} loading={changeLoading} disabled={changeLoading} onPress={onChangeEmail}/>
                </View>
              </View>) : null}
            {statusMessage ? (<Text style={styles.status}>{statusMessage}</Text>) : null}
            <View style={styles.supportDivider}/>
            <Text style={styles.supportLabel}>Still stuck?</Text>
            <Text style={styles.support}>
              If nothing arrives after about 15 minutes, email{' '}
              <Text style={styles.linkInline} onPress={() => {
            Linking.openURL(`mailto:${SUPPORT_EMAIL}`);
        }}>
                {SUPPORT_EMAIL}
              </Text>{' '}
              and mention the address you signed up with.
            </Text>
          </View>
        </View>
      </ScrollView>
    </ContentView>);
};
const styles = StyleSheet.create({
    outer: {
        flex: 1,
    },
    scroll: {
        flexGrow: 1,
        paddingHorizontal: 20,
        paddingTop: 28,
        paddingBottom: 40,
        alignItems: 'center',
    },
    pageColumn: {
        width: '100%',
        maxWidth: PAGE_MAX_WIDTH,
        alignItems: 'center',
    },
    logo: {
        width: LOGO_WIDTH,
        height: LOGO_HEIGHT,
        marginBottom: 24,
    },
    card: {
        width: '100%',
        maxWidth: CARD_MAX_WIDTH,
        alignSelf: 'center',
        backgroundColor: theme.colors.textOnPrimary,
        borderRadius: 12,
        paddingHorizontal: 24,
        paddingTop: 28,
        paddingBottom: 24,
        borderWidth: 1,
        borderColor: theme.colors.borderSoft,
        shadowColor: theme.colors.shadow,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
    },
    title: {
        fontSize: 22,
        fontWeight: '600',
        color: theme.colors.textTitle,
        marginBottom: 16,
        textAlign: 'center',
    },
    lead: {
        fontSize: 16,
        lineHeight: 24,
        marginBottom: 16,
        color: theme.colors.textTitle,
        textAlign: 'left',
    },
    body: {
        fontSize: 15,
        lineHeight: 22,
        marginBottom: 12,
        color: theme.colors.textTitle,
        textAlign: 'left',
    },
    bodyMuted: {
        fontSize: 14,
        lineHeight: 21,
        marginBottom: 8,
        color: theme.colors.textPlaceholderStrong,
        textAlign: 'left',
    },
    emailEmphasis: {
        fontWeight: '600',
        color: theme.colors.textTitle,
    },
    actions: {
        marginTop: 8,
        width: '100%',
    },
    actionsTight: {
        marginTop: 8,
        width: '100%',
    },
    linkRow: {
        alignSelf: 'stretch',
        marginTop: 16,
        paddingVertical: 8,
    },
    linkRowPressed: {
        opacity: 0.7,
    },
    linkText: {
        fontSize: 15,
        color: theme.colors.textLinkVariant,
        textDecorationLine: 'underline',
        textAlign: 'left',
    },
    changeBox: {
        marginTop: 8,
        padding: 16,
        borderRadius: 8,
        backgroundColor: theme.colors.backgroundScreenAlt,
        borderWidth: 1,
        borderColor: theme.colors.borderMuted,
        width: '100%',
    },
    changeHeading: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.textTitle,
        marginBottom: 12,
    },
    label: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.textBody,
        marginBottom: 6,
        marginTop: 4,
    },
    input: {
        borderWidth: 1,
        borderColor: theme.colors.controlInactive,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        fontSize: 16,
        backgroundColor: theme.colors.textOnPrimary,
    },
    status: {
        marginTop: 16,
        fontSize: 14,
        lineHeight: 20,
        color: theme.colors.successDeep,
        textAlign: 'left',
    },
    supportDivider: {
        height: 1,
        backgroundColor: theme.colors.borderSoft,
        marginTop: 24,
        marginBottom: 16,
        alignSelf: 'stretch',
    },
    supportLabel: {
        fontSize: 13,
        fontWeight: '600',
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        marginBottom: 8,
        textAlign: 'left',
        alignSelf: 'stretch',
    },
    support: {
        fontSize: 14,
        lineHeight: 21,
        color: theme.colors.textCaption,
        textAlign: 'left',
        alignSelf: 'stretch',
    },
    linkInline: {
        color: theme.colors.textLinkVariant,
        textDecorationLine: 'underline',
    },
});
